import { QuestionCircleOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import { useState } from "react";
import type { ReactNode } from "react";

interface Props {
  /** What the «(?)» explains: the name of the tab, menu item or field it stands next to. */
  subject: string;
  text: ReactNode;
  /** Smaller icon for dense places (menu items, field labels). */
  size?: number;
  /** UXW-56: the «(?)» of a form field is named «О поле «…»» for the screen reader; tabs and menu keep «Подсказка». */
  field?: boolean;
}

/**
 * UI-19 (ТЗ «Понятные формы», 29.10.2026): the «(?)» next to a tab, a menu item or a field label. Opens on hover,
 * on click/tap and from the keyboard; the click never reaches the tab or the link under it, Escape and a second
 * press close it. One popover at a time: antd closes the previous on the next open.
 */
export function HelpHint({ subject, text, size = 14, field = false }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      content={<div style={{ maxWidth: 320, whiteSpace: "normal" }}>{text}</div>}
      title={subject}
      trigger={["hover", "click"]}
      open={open}
      onOpenChange={setOpen}
    >
      <span
        role="button"
        tabIndex={0}
        aria-label={field ? `О поле «${subject}»` : `Подсказка: ${subject}`}
        data-testid="help-hint"
        className="wms-help-hint"
        style={{ display: "inline-flex", alignItems: "center", marginLeft: 6, color: "var(--wms-muted)", cursor: "help", fontSize: size, lineHeight: 1 }}
        onClick={(e) => {
          // antd's own click trigger toggles the popover; here only the tab or link under the icon is kept quiet
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        <QuestionCircleOutlined />
      </span>
    </Popover>
  );
}
